/**
 * Lazy Sentry helpers — never crash the caller, never eagerly import Sentry.
 *
 * We dynamically import inside a try/catch rather than using a top-level
 * import because Sentry's subdeps pull a second copy of React at module-parse
 * time on web, colliding with Expo's bundled React ("Cannot read properties of
 * null (reading useState)"). This pattern was established in
 * lib/hooks/use-payment.ts after commit 8fe78d3 and must be mirrored anywhere
 * we want to capture errors outside payment code.
 *
 * The import target is `./sentry`, never `@sentry/react-native` directly:
 * Metro picks `sentry.web.ts` on web and `sentry.ts` on native, so each
 * platform loads exactly one SDK. Naming the native package here put the
 * whole React Native SDK — its own nested @sentry/core, @sentry/browser,
 * replay and feedback copies, ~1.1 MB — into the web bundle alongside the
 * @sentry/react build that web actually initialises.
 *
 * Usage:
 *   import { captureError, addBreadcrumb } from '@/lib/utils/observability'
 *   try {
 *     await doThing()
 *   } catch (err) {
 *     captureError(err, { screen: 'messages', action: 'send' })
 *     toast.error(err instanceof Error ? err.message : t('errors.generic'))
 *   }
 */
import i18n from '../i18n/config'
import { isExpectedNonFatalOperationalError } from '../errors/service-error'
import { normalizeCapturedError } from './sentry-capture'

export function captureError(
  err: unknown,
  tags: Record<string, string>,
  extra?: Record<string, unknown>,
): void {
  try {
    const message = err instanceof Error ? err.message : String(err)
    if (isExpectedNonFatalOperationalError(err)) {
      void import('./sentry')
        .then(({ Sentry }) => {
          Sentry.addBreadcrumb({
            category: 'operational',
            level: 'warning',
            message,
            data: tags,
          })
        })
        .catch(() => {})
      return
    }

    void import('./sentry')
      .then(({ Sentry }) => {
        Sentry.captureException(normalizeCapturedError(err), {
          tags,
          extra: { ...extra, message },
        })
      })
      .catch(() => {})
  } catch {
    // Never let observability fail the caller.
  }
}

export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>,
): void {
  try {
    void import('./sentry')
      .then(({ Sentry }) => {
        Sentry.addBreadcrumb({ category, level: 'info', message, data })
      })
      .catch(() => {})
  } catch {
    // ignore
  }
}

/**
 * Extract a user-ready error message from any thrown value. Falls back to the
 * provided generic when the error has no usable message.
 *
 * Resolution order:
 *  1. ServiceError.userMessageKey → resolve via i18next. Service-layer
 *     errors carry a friendly i18n key (e.g. 'errors.unique_violation')
 *     mapped from the underlying SQLSTATE / Supabase code, so toasts
 *     never surface raw "duplicate key value violates unique constraint
 *     <table>_<col>_<col>_key" gibberish.
 *  2. __DEV__ → raw err.message so engineers can see what actually broke
 *     without opening Sentry.
 *  3. Production fallback → the i18n-friendly string the caller provided.
 *
 * Why this matters: user-facing toasts must NEVER include SDK / Postgres
 * internals. Engineers still get the full error via `captureError()` and
 * via console.error in catch sites. The toast surface stays i18n-controlled.
 */
export function errorMessage(err: unknown, fallback: string): string {
  // 1. ServiceError-provided i18n key
  const userKey = (err as { userMessageKey?: string | null })?.userMessageKey
  if (userKey) {
    try {
      const translated = i18n.t(userKey)
      if (translated && translated !== userKey) return translated
    } catch {
      // i18n unavailable (test runner, etc.) — fall through.
    }
  }

  // 2. Dev: raw message so engineers see the SQLSTATE locally.
  if (
    typeof __DEV__ !== 'undefined' &&
    __DEV__ &&
    err instanceof Error &&
    err.message &&
    err.message !== 'Error'
  ) {
    return err.message
  }

  // 3. Caller-provided friendly fallback.
  return fallback
}
